import { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Send, AlertCircle, Eye, Menu, Tag, FolderTree } from 'lucide-react';

export default function SendMenu({ selectedTeam }) {
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [messages, setMessages] = useState({});
  const [loading, setLoading] = useState(false);
  const [teamInfo, setTeamInfo] = useState(null);
  const [targetChannels, setTargetChannels] = useState(['main']);

  useEffect(() => {
    if (selectedTeam) {
      loadPreviewData();
      loadTeamInfo();
    }
  }, [selectedTeam]);

  const loadTeamInfo = async () => {
    try {
      const res = await axiosInstance.get(`/teams/${selectedTeam}`);
      setTeamInfo(res.data);
      // Auto-enable info channel checkbox by default if configured
      if (res.data.info_channel_id) {
        setTargetChannels(['main', 'info']);
      } else {
        setTargetChannels(['main']);
      }
    } catch (err) {
      console.error('Team info load failed', err);
    }
  };

  const toggleTargetChannel = (channel) => {
    setTargetChannels(prev =>
      prev.includes(channel)
        ? prev.filter(c => c !== channel)
        : [...prev, channel]
    );
  };

  const loadPreviewData = async () => {
    setLoading(true);
    try {
      const [itemsRes, brandsRes, catsRes, msgsRes] = await Promise.all([
        axiosInstance.get(`/teams/${selectedTeam}/menu-items`),
        axiosInstance.get(`/teams/${selectedTeam}/brands`),
        axiosInstance.get(`/teams/${selectedTeam}/categories`),
        axiosInstance.get(`/teams/${selectedTeam}/messages`)
      ]);
      setMenuItems(itemsRes.data);
      setBrands(brandsRes.data);
      setCategories(catsRes.data);
      
      const msgDict = {};
      msgsRes.data.forEach(msg => {
        msgDict[msg.key] = msg.value;
      });
      setMessages(msgDict);
    } catch (err) {
      toast.error('Önizleme yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMenu = async () => {
    if (!targetChannels || targetChannels.length === 0) {
      toast.error('En az bir hedef kanal seçin');
      return;
    }
    setSending(true);
    try {
      await axiosInstance.post('/telegram/send-menu', {
        team_id: selectedTeam,
        target_channels: targetChannels
      });
      toast.success('Menü başarıyla gönderildi!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Menü gönderilemedi');
    } finally {
      setSending(false);
    }
  };

  if (!selectedTeam) {
    return (
      <div className="flex items-center justify-center h-full">
        <Card className="w-96 text-center overflow-hidden">
          <CardContent className="pt-12 pb-12">
            <Send className="w-16 h-16 mx-auto mb-4 text-purple-500" />
            <h2 className="text-2xl font-bold mb-2">Mağaza Seçin</h2>
            <p className="text-gray-600">Menü göndermek için bir mağaza seçin.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="send-menu-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Menü Gönder</h1>
        <p className="text-gray-600">Bot menüsünü Telegram kanalına gönderin</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <Card className="shadow-lg border-0 overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
              <CardTitle className="flex items-center">
                <Send className="w-6 h-6 mr-2" />
                Menü Gönder
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              {/* Hedef Kanal Seçimi */}
              <div className="space-y-3 p-4 bg-blue-50 rounded-2xl border border-blue-100">
                <p className="text-sm font-semibold text-gray-800">Hedef Kanal Seçimi</p>
                <p className="text-xs text-gray-600">Menünün hangi kanal(lar)a gönderileceğini seçin.</p>
                <label className="flex items-center space-x-3 p-3 bg-white rounded-xl cursor-pointer hover:bg-blue-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={targetChannels.includes('main')}
                    onChange={() => toggleTargetChannel('main')}
                    className="w-5 h-5 accent-blue-500"
                    data-testid="menu-target-main-channel"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-sm">Ana Kanal</p>
                    <p className="text-xs text-gray-500">{teamInfo?.channel_id || 'Ayarlanmamış'}</p>
                  </div>
                </label>
                <label
                  className={`flex items-center space-x-3 p-3 rounded-xl transition-colors ${
                    teamInfo?.info_channel_id
                      ? 'bg-white cursor-pointer hover:bg-cyan-50'
                      : 'bg-gray-100 opacity-60 cursor-not-allowed'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={targetChannels.includes('info')}
                    onChange={() => toggleTargetChannel('info')}
                    disabled={!teamInfo?.info_channel_id}
                    className="w-5 h-5 accent-cyan-500"
                    data-testid="menu-target-info-channel"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-sm">Info Kanal</p>
                    <p className="text-xs text-gray-500">
                      {teamInfo?.info_channel_id || 'Mağaza ayarlarında henüz yapılandırılmadı'}
                    </p>
                  </div>
                </label>
              </div>

              <Button 
                onClick={handleSendMenu}
                disabled={sending}
                className="w-full h-14 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                data-testid="send-menu-button"
              >
                {sending ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Gönderiliyor...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Menüyü Şimdi Gönder
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Preview - Always visible */}
        <div className="space-y-4">
            <Card className="shadow-lg border-0 overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-green-500 to-teal-500 text-white">
                <CardTitle className="flex items-center">
                  <Eye className="w-6 h-6 mr-2" />
                  Menü Önizleme
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Menu Items */}
                    <div className="bg-gray-50 rounded-2xl p-4">
                      <div className="flex items-center mb-3">
                        <Menu className="w-5 h-5 mr-2 text-purple-600" />
                        <h3 className="font-semibold text-gray-800">1. Hoşgeldin Mesajı</h3>
                      </div>
                      <p className="text-sm text-gray-700 mb-3 whitespace-pre-wrap">{messages.WelcomeMessage || 'Hoşgeldiniz'}</p>
                      <div className="space-y-2">
                        {menuItems.map(item => (
                          <div key={item.id} className="bg-blue-500 text-white text-center py-2 px-4 rounded-2xl text-sm font-medium">
                            {item.text}
                          </div>
                        ))}
                      </div>
                      {menuItems.length === 0 && (
                        <p className="text-xs text-gray-400 text-center">Hiç menü butonu yok</p>
                      )}
                    </div>

                    {/* Brands */}
                    <div className="bg-gray-50 rounded-2xl p-4">
                      <div className="flex items-center mb-3">
                        <Tag className="w-5 h-5 mr-2 text-purple-600" />
                        <h3 className="font-semibold text-gray-800">2. Markalar</h3>
                      </div>
                      <p className="text-sm text-gray-700 mb-3 whitespace-pre-wrap">{messages.BrandsMessage || 'Markalarımız'}</p>
                      <div className="grid grid-cols-2 gap-2">
                        {brands.slice(0, 10).map(brand => (
                          <div key={brand.id} className="bg-purple-500 text-white text-center py-2 px-3 rounded-2xl text-xs font-medium truncate">
                            {brand.name}
                          </div>
                        ))}
                      </div>
                      {brands.length > 10 && (
                        <p className="text-xs text-gray-500 mt-2 text-center">+{brands.length - 10} daha fazla</p>
                      )}
                      {brands.length === 0 && (
                        <p className="text-xs text-gray-400 text-center">Hiç marka yok</p>
                      )}
                    </div>

                    {/* Categories */}
                    <div className="bg-gray-50 rounded-2xl p-4">
                      <div className="flex items-center mb-3">
                        <FolderTree className="w-5 h-5 mr-2 text-purple-600" />
                        <h3 className="font-semibold text-gray-800">3. Kategoriler</h3>
                      </div>
                      <p className="text-sm text-gray-700 mb-3 whitespace-pre-wrap">{messages.CategoriesMessage || 'Kategoriler'}</p>
                      <div className="grid grid-cols-2 gap-2">
                        {categories.slice(0, 10).map(cat => (
                          <div key={cat.id} className="bg-pink-500 text-white text-center py-2 px-3 rounded-2xl text-xs font-medium truncate">
                            {cat.name}
                          </div>
                        ))}
                      </div>
                      {categories.length > 10 && (
                        <p className="text-xs text-gray-500 mt-2 text-center">+{categories.length - 10} daha fazla</p>
                      )}
                      {categories.length === 0 && (
                        <p className="text-xs text-gray-400 text-center">Hiç kategori yok</p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
      </div>
    </div>
  );
}
